import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Send, Upload, CheckCircle2, FileText, User, Mail, Phone, Briefcase, Languages, GraduationCap } from 'lucide-react';

const ApplicationPage = ({ t }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Personal Information
    fullName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    address: '',
    
    // Professional Information
    position: '',
    experience: '',
    education: '',
    currentEmployment: '',
    
    // Skills & Languages
    germanLevel: '',
    englishLevel: '',
    skills: '',
    
    // Additional Information
    message: '',
    cvFile: null,
    availability: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleFileChange = (e) => {
    setFormData({
      ...formData,
      cvFile: e.target.files[0]
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare data for backend API (only fields expected by backend)
      const applicationData = {
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        position: formData.position,
        experience: formData.experience,
        message: formData.message || 'No additional message'
      };

      // Call backend API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(applicationData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit application');
      }

      const result = await response.json();
      console.log('Application submitted:', result);
      
      toast.success(t.application.success);
      
      // Navigate to home after successful submission
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error(t.application.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = () => {
    if (currentStep < 3) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-50 via-white to-gray-50 pt-24 pb-16 px-4 overflow-hidden">
      {/* Professional Background */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Subtle gradient accents */}
        <div className="absolute top-20 right-10 w-96 h-96 bg-gradient-to-br from-red-100/40 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-gradient-to-tr from-orange-100/30 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-gradient-to-bl from-red-50/30 to-transparent rounded-full blur-2xl"></div>
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: 'linear-gradient(rgba(220, 38, 38, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(220, 38, 38, 0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }}></div>
      </div>
      
      <div className="container mx-auto max-w-4xl relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-full text-red-700 text-sm font-semibold mb-4">
            <FileText className="h-4 w-4" />
            Official Application Form
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Apply for Work in Europe
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Join hundreds of successful Albanian workers who found their dream careers in Germany through Majestic Workforce
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            <div className="flex-1">
              <div className={`h-2 rounded-full transition-all ${currentStep >= 1 ? 'bg-gradient-to-r from-red-600 to-orange-600' : 'bg-gray-200'}`}></div>
              <div className="text-center mt-2">
                <div className={`text-sm font-semibold ${currentStep >= 1 ? 'text-red-600' : 'text-gray-400'}`}>
                  Personal Info
                </div>
              </div>
            </div>
            <div className="w-8"></div>
            <div className="flex-1">
              <div className={`h-2 rounded-full transition-all ${currentStep >= 2 ? 'bg-gradient-to-r from-red-600 to-orange-600' : 'bg-gray-200'}`}></div>
              <div className="text-center mt-2">
                <div className={`text-sm font-semibold ${currentStep >= 2 ? 'text-red-600' : 'text-gray-400'}`}>
                  Professional
                </div>
              </div>
            </div>
            <div className="w-8"></div>
            <div className="flex-1">
              <div className={`h-2 rounded-full transition-all ${currentStep >= 3 ? 'bg-gradient-to-r from-red-600 to-orange-600' : 'bg-gray-200'}`}></div>
              <div className="text-center mt-2">
                <div className={`text-sm font-semibold ${currentStep >= 3 ? 'text-red-600' : 'text-gray-400'}`}>
                  Final Details
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Application Form */}
        <Card className="border border-gray-200 shadow-xl bg-white">
          <CardHeader className="border-b border-gray-200 bg-gradient-to-r from-red-50 to-orange-50">
            <CardTitle className="text-2xl flex items-center gap-3 text-gray-900">
              {currentStep === 1 && <><User className="h-6 w-6 text-red-600" /> Personal Information</>}
              {currentStep === 2 && <><Briefcase className="h-6 w-6 text-red-600" /> Professional Background</>}
              {currentStep === 3 && <><CheckCircle2 className="h-6 w-6 text-red-600" /> Additional Details</>}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <form onSubmit={handleSubmit}>
              {/* Step 1: Personal Information */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="fullName" className="text-gray-700 font-medium flex items-center gap-2">
                        <User className="h-4 w-4 text-red-600" />
                        Full Name *
                      </Label>
                      <Input
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        className="h-12"
                        placeholder="John Doe"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="dateOfBirth" className="text-gray-700 font-medium">
                        Date of Birth *
                      </Label>
                      <Input
                        id="dateOfBirth"
                        name="dateOfBirth"
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        required
                        className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500 h-12"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-gray-700 font-medium flex items-center gap-2">
                        <Mail className="h-4 w-4 text-emerald-600" />
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500 h-12"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-gray-700 font-medium flex items-center gap-2">
                        <Phone className="h-4 w-4 text-emerald-600" />
                        Phone Number *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500 h-12"
                        placeholder="+355 69 123 4567"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address" className="text-gray-700 font-medium">
                      Current Address *
                    </Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500 h-12"
                      placeholder="Street, City, Albania"
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Professional Information */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="position" className="text-gray-700 font-medium flex items-center gap-2">
                        <Briefcase className="h-4 w-4 text-emerald-600" />
                        Position Interested In *
                      </Label>
                      <Select onValueChange={(value) => handleSelectChange('position', value)} required>
                        <SelectTrigger className="h-12 border-slate-300">
                          <SelectValue placeholder="Select position" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="construction">Construction Worker</SelectItem>
                          <SelectItem value="nurse">Nurse / Healthcare</SelectItem>
                          <SelectItem value="warehouse">Warehouse Worker</SelectItem>
                          <SelectItem value="restaurant">Restaurant Staff</SelectItem>
                          <SelectItem value="electrician">Electrician</SelectItem>
                          <SelectItem value="hotel">Hotel Staff</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="experience" className="text-gray-700 font-medium">
                        Years of Experience *
                      </Label>
                      <Select onValueChange={(value) => handleSelectChange('experience', value)} required>
                        <SelectTrigger className="h-12 border-slate-300">
                          <SelectValue placeholder="Select experience" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0-1">0-1 years</SelectItem>
                          <SelectItem value="1-3">1-3 years</SelectItem>
                          <SelectItem value="3-5">3-5 years</SelectItem>
                          <SelectItem value="5-10">5-10 years</SelectItem>
                          <SelectItem value="10+">10+ years</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="education" className="text-gray-700 font-medium flex items-center gap-2">
                        <GraduationCap className="h-4 w-4 text-emerald-600" />
                        Education Level *
                      </Label>
                      <Select onValueChange={(value) => handleSelectChange('education', value)} required>
                        <SelectTrigger className="h-12 border-slate-300">
                          <SelectValue placeholder="Select education" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="secondary">Secondary School</SelectItem>
                          <SelectItem value="vocational">Vocational Training</SelectItem>
                          <SelectItem value="bachelor">Bachelor&apos;s Degree</SelectItem>
                          <SelectItem value="master">Master&apos;s Degree</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="currentEmployment" className="text-gray-700 font-medium">
                        Current Employment Status *
                      </Label>
                      <Select onValueChange={(value) => handleSelectChange('currentEmployment', value)} required>
                        <SelectTrigger className="h-12 border-slate-300">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="employed">Currently Employed</SelectItem>
                          <SelectItem value="unemployed">Unemployed</SelectItem>
                          <SelectItem value="student">Student</SelectItem>
                          <SelectItem value="self-employed">Self-Employed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="germanLevel" className="text-gray-700 font-medium flex items-center gap-2">
                        <Languages className="h-4 w-4 text-emerald-600" />
                        German Language Level *
                      </Label>
                      <Select onValueChange={(value) => handleSelectChange('germanLevel', value)} required>
                        <SelectTrigger className="h-12 border-slate-300">
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Knowledge</SelectItem>
                          <SelectItem value="a1">A1 - Beginner</SelectItem>
                          <SelectItem value="a2">A2 - Elementary</SelectItem>
                          <SelectItem value="b1">B1 - Intermediate</SelectItem>
                          <SelectItem value="b2">B2 - Upper Intermediate</SelectItem>
                          <SelectItem value="c1">C1 - Advanced</SelectItem>
                          <SelectItem value="c2">C2 - Proficient</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="englishLevel" className="text-gray-700 font-medium">
                        English Language Level *
                      </Label>
                      <Select onValueChange={(value) => handleSelectChange('englishLevel', value)} required>
                        <SelectTrigger className="h-12 border-slate-300">
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Knowledge</SelectItem>
                          <SelectItem value="basic">Basic</SelectItem>
                          <SelectItem value="intermediate">Intermediate</SelectItem>
                          <SelectItem value="advanced">Advanced</SelectItem>
                          <SelectItem value="fluent">Fluent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Additional Details */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="skills" className="text-gray-700 font-medium">
                      Skills & Qualifications
                    </Label>
                    <Textarea
                      id="skills"
                      name="skills"
                      value={formData.skills}
                      onChange={handleChange}
                      rows={4}
                      className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500 resize-none"
                      placeholder="List your relevant skills, certifications, and qualifications..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="availability" className="text-gray-700 font-medium">
                      Availability to Start *
                    </Label>
                    <Select onValueChange={(value) => handleSelectChange('availability', value)} required>
                      <SelectTrigger className="h-12 border-slate-300">
                        <SelectValue placeholder="Select availability" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="immediate">Immediately</SelectItem>
                        <SelectItem value="1-month">Within 1 month</SelectItem>
                        <SelectItem value="2-months">Within 2 months</SelectItem>
                        <SelectItem value="3-months">Within 3 months</SelectItem>
                        <SelectItem value="negotiable">Negotiable</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cvFile" className="text-gray-700 font-medium flex items-center gap-2">
                      <Upload className="h-4 w-4 text-emerald-600" />
                      Upload CV/Resume (Optional)
                    </Label>
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-emerald-500 transition-colors">
                      <Input
                        id="cvFile"
                        name="cvFile"
                        type="file"
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx"
                        className="hidden"
                      />
                      <label htmlFor="cvFile" className="cursor-pointer">
                        <Upload className="h-10 w-10 text-slate-400 mx-auto mb-2" />
                        <p className="text-slate-600 font-medium">
                          {formData.cvFile ? formData.cvFile.name : 'Click to upload or drag and drop'}
                        </p>
                        <p className="text-sm text-slate-500 mt-1">PDF, DOC, DOCX (max. 5MB)</p>
                      </label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-gray-700 font-medium">
                      Additional Information
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500 resize-none"
                      placeholder="Tell us why you want to work in Germany, your career goals, or any other relevant information..."
                    />
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={currentStep === 1 ? () => navigate('/') : prevStep}
                  className="border-2 border-slate-300 hover:border-slate-400 px-6"
                >
                  {currentStep === 1 ? 'Cancel' : 'Previous'}
                </Button>

                {currentStep < 3 ? (
                  <Button
                    type="button"
                    onClick={nextStep}
                    className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 px-8 text-white border-0"
                  >
                    Next Step
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 px-8 text-white border-0"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                        Submitting...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Submit Application
                        <Send className="h-5 w-5" />
                      </span>
                    )}
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Trust Indicators */}
        <div className="mt-12 grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
          <div className="text-center">
            <div className="w-12 h-12 bg-gradient-to-br from-red-600/20 to-orange-600/20 rounded-full flex items-center justify-center mx-auto mb-3 ring-2 ring-orange-600/30">
              <CheckCircle2 className="h-6 w-6 text-red-600" />
            </div>
            <div className="font-semibold text-white">Verified Agency</div>
            <div className="text-sm text-gray-400">Licensed & Certified</div>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-gradient-to-br from-red-600/20 to-orange-600/20 rounded-full flex items-center justify-center mx-auto mb-3 ring-2 ring-orange-600/30">
              <CheckCircle2 className="h-6 w-6 text-red-600" />
            </div>
            <div className="font-semibold text-white">24h Response</div>
            <div className="text-sm text-gray-400">Quick Processing</div>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-gradient-to-br from-red-600/20 to-orange-600/20 rounded-full flex items-center justify-center mx-auto mb-3 ring-2 ring-orange-600/30">
              <CheckCircle2 className="h-6 w-6 text-red-600" />
            </div>
            <div className="font-semibold text-white">Free Service</div>
            <div className="text-sm text-gray-400">No Hidden Fees</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationPage;
